import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
    VStack,
    TableContainer,
    useColorMode,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Stack,
} from '@chakra-ui/react';
import {
    AiOutlineSearch,
    AiOutlineDelete,
    AiOutlineEdit,
    AiOutlinePlus,
    AiOutlineContacts,
    AiOutlineMail,
    AiOutlinePhone,
    AiOutlineUser,
    AiOutlineBank,
    AiOutlineIdcard,
    AiOutlineEye,
    AiOutlineCalendar,
    AiOutlineFilter,
    AiOutlineMinus,
} from 'react-icons/ai';
import { useComercial } from '../../../../contexts/ComercialContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useModalMinimizer } from '../../../../contexts/ModalMinimizerContext';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import SearchableSelect from '../../../../components/UI/SearchableSelect';
import TablePagination from '../../../../components/UI/TablePagination';
import api from '../../../../services/api';

const FORM_INICIAL = {
    nome: '',
    empresa: '',
    company_id: null,
    telefone: '',
    email: '',
    cargo: '',
    cargo_id: null,
    canal: '',
    source_channel_id: null,
    assignee_id: null,
    observacoes: '',
};

// ─── DetailItem (usado no modal de visualização) ─────────────────────────────
const DetailItem = ({ icon, label, value, valueColor }) => {
    const { colorMode } = useColorMode();
    return (
        <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="lg" px={3} py={2.5}>
            <HStack spacing={1.5} mb={0.5}>
                <Icon as={icon} color="textSecondary" boxSize="11px" />
                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">
                    {label}
                </Text>
            </HStack>
            <Text fontSize="sm" color={valueColor || 'textPrimary'} fontWeight="700" lineHeight="1.3">
                {value || '—'}
            </Text>
        </Box>
    );
};

const Contatos = () => {
    const { colorMode } = useColorMode();
    const { contatos, addContato, updateContato, deleteContato } = useComercial();
    const { marcas } = useNogleTech();
    const { employees } = usePermissions();
    const toast = useToast();

    // Modals
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

    // Dropdowns do banco
    const [cargos, setCargos] = useState([]);
    const [canais, setCanais] = useState([]);

    const [busca, setBusca] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const [contatoEditando, setContatoEditando] = useState(null);
    const [contatoVisualizando, setContatoVisualizando] = useState(null);
    const [contatoExcluindo, setContatoExcluindo] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    
    // Autocompletes states
    const [empresaFocada, setEmpresaFocada] = useState(false);
    const [emailFocado, setEmailFocado] = useState(false);

    // Carregar dropdowns dinâmicos
    const fetchDropdowns = useCallback(async () => {
        try {
            const [resCargos, resCanais] = await Promise.all([
                api.get('/comercial/dropdowns/cargos'),
                api.get('/comercial/dropdowns/canais')
            ]);
            setCargos(resCargos.data || []);
            setCanais(resCanais.data || []);
        } catch (err) {
            console.error('Erro ao carregar dropdowns:', err);
        }
    }, []);

    useEffect(() => {
        fetchDropdowns();
    }, [fetchDropdowns]);

    const marcasSugeridas = useMemo(() => {
        const query = form.empresa?.trim().toLowerCase();
        if (!query) return [];
        return marcas.filter(m => m.nome?.toLowerCase().includes(query)).slice(0, 8);
    }, [marcas, form.empresa]);

    const employeesSugeridos = useMemo(() => {
        const query = form.email?.trim().toLowerCase();
        if (!query) return [];
        return employees.filter(e => 
            e.email?.toLowerCase().includes(query) || 
            e.name?.toLowerCase().includes(query)
        ).slice(0, 8);
    }, [employees, form.email]);

    const contatosFiltrados = useMemo(() => {
        let lista = [...contatos];
        if (filterEmployeeIds.length > 0) {
            lista = lista.filter(c => filterEmployeeIds.includes(String(c.assignee_id)));
        }
        if (busca.trim()) {
            const q = busca.toLowerCase();
            lista = lista.filter(c =>
                c.nome?.toLowerCase().includes(q) ||
                c.empresa?.toLowerCase().includes(q) ||
                c.email?.toLowerCase().includes(q)
            );
        }
        return lista;
    }, [contatos, busca, filterEmployeeIds]);

    const totalPages = Math.max(1, Math.ceil(contatosFiltrados.length / itemsPerPage));
    const paginatedContatos = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return contatosFiltrados.slice(start, start + itemsPerPage);
    }, [contatosFiltrados, currentPage, itemsPerPage]);

    // Reset pagination when search or filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [busca, filterEmployeeIds]);

    useEffect(() => {
        setCurrentPage(page => Math.min(page, totalPages));
    }, [totalPages]);

    // ── Visualizar ────────────────────────────────────────────────
    const handleVisualizar = useCallback((contato) => {
        setContatoVisualizando(contato);
        onViewOpen();
    }, [onViewOpen]);

    const handleFecharVisualizar = useCallback(() => {
        setContatoVisualizando(null);
        onViewClose();
    }, [onViewClose]);

    // ── Editar ────────────────────────────────────────────────────
    const handleAbrir = useCallback((contato = null) => {
        setContatoEditando(contato);
        if (contato) {
            setForm({
                nome: contato.nome || '',
                empresa: contato.empresa || '',
                company_id: contato.company_id || null,
                telefone: contato.telefone || '',
                email: contato.email || '',
                cargo: contato.cargo || '',
                cargo_id: contato.cargo_id || null,
                canal: contato.canal || '',
                source_channel_id: contato.source_channel_id || null,
                assignee_id: contato.assignee_id || null,
                observacoes: contato.observacoes || '',
            });
        } else {
            setForm(FORM_INICIAL);
        }
        onOpen();
    }, [onOpen]);

    const { minimizeModal, removeMinimizedModal, consumePendingRestore } = useModalMinimizer();

    const handleMinimizar = useCallback(() => {
        onClose();
        const modalId = 'contato-modal';
        const title = contatoEditando ? 'Editar Contato' : 'Novo Contato';
        const subtitle = form.nome || form.empresa || 'Rascunho de Contato';
        minimizeModal({
            id: modalId,
            title,
            subtitle,
            icon: AiOutlineContacts,
            route: '/comercial/contatos',
            formData: { form, contatoEditando },
            onExpand: () => {
                onOpen();
            },
            onClose: () => {
                setContatoEditando(null);
                setForm(FORM_INICIAL);
            }
        });
    }, [onClose, onOpen, contatoEditando, form, minimizeModal]);

    useEffect(() => {
        const pendingData = consumePendingRestore('contato-modal');
        if (pendingData) {
            if (pendingData.form) setForm(pendingData.form);
            if (pendingData.contatoEditando !== undefined) setContatoEditando(pendingData.contatoEditando);
            onOpen();
        }
    }, [consumePendingRestore, onOpen]);

    const handleFechar = useCallback(() => {
        setContatoEditando(null);
        setForm(FORM_INICIAL);
        removeMinimizedModal('contato-modal');
        onClose();
    }, [onClose, removeMinimizedModal]);

    const handleEditarDoView = useCallback(() => {
        handleFecharVisualizar();
        handleAbrir(contatoVisualizando);
    }, [contatoVisualizando, handleFecharVisualizar, handleAbrir]);

    const handleSalvar = useCallback(async () => {
        if (!form.nome.trim()) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        try {
            if (contatoEditando) {
                await updateContato(contatoEditando.id, form);
                toast({ title: 'Contato atualizado', status: 'success', duration: 2000 });
            } else {
                await addContato(form);
                toast({ title: 'Contato adicionado', status: 'success', duration: 2000 });
            }
            handleFechar();
        } catch (err) {
            console.error('Erro ao salvar contato:', err);
            toast({ title: 'Erro ao salvar', status: 'error', duration: 3500 });
        }
    }, [form, contatoEditando, addContato, updateContato, handleFechar, toast]);

    // ── Excluir ────────────────────────────────────────────────────
    const handleAbrirExcluir = useCallback((contato) => {
        setContatoExcluindo(contato);
        onDelOpen();
    }, [onDelOpen]);

    const handleExcluirDoView = useCallback(() => {
        handleFecharVisualizar();
        handleAbrirExcluir(contatoVisualizando);
    }, [contatoVisualizando, handleFecharVisualizar, handleAbrirExcluir]);

    const handleConfirmarExcluir = useCallback(async () => {
        if (contatoExcluindo) {
            try {
                await deleteContato(contatoExcluindo.id);
                toast({ title: `"${contatoExcluindo.nome}" removido`, status: 'info', duration: 2000 });
            } catch (err) {
                console.error(err);
                toast({ title: 'Erro ao excluir', status: 'error', duration: 3000 });
            }
        }
        setContatoExcluindo(null);
        onDelClose();
    }, [contatoExcluindo, deleteContato, onDelClose, toast]);

    const getInitials = (nome) => {
        if (!nome) return '?';
        return nome.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
    };

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="100%">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">Contatos</Heading>
                </Box>
                <Flex gap={3} wrap="wrap" justify="flex-end" flex="1" minW="0" align="center">
                    <InputGroup maxW={{ base: '100%', md: '280px' }} minW="160px" flex="1 1 200px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar por nome, empresa..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                            color="textPrimary"
                            borderRadius="xl"
                        />
                    </InputGroup>
                    <Popover placement="bottom-end" closeOnBlur={true}>
                        <PopoverTrigger>
                            <Button
                                size="sm"
                                variant="outline"
                                borderColor="borderPrimary"
                                color="textPrimary"
                                leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />}
                                _hover={{ bg: 'surfaceHover' }}
                                borderRadius="xl"
                                fontWeight="800"
                                flexShrink={0}
                            >
                                Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" borderRadius="xl" _focus={{ boxShadow: 'none' }} zIndex={999}>
                            <PopoverArrow bg="surface" />
                            <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">
                                Filtrar Funcionários
                            </PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="400px" overflowY="auto" p={0}>
                                <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                                    <Input
                                        placeholder="Buscar..."
                                        size="sm"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)}
                                        mb={2}
                                        borderRadius="md"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="green"
                                        w="100%"
                                        onClick={() => setFilterEmployeeIds([])}
                                        isDisabled={filterEmployeeIds.length === 0}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Box>
                                <Stack spacing={0}>
                                    {employees
                                        ?.filter(emp => {
                                            if (!filterSearch) return true;
                                            return emp.name?.toLowerCase().includes(filterSearch.toLowerCase());
                                        })
                                        .map((emp) => {
                                            const isChecked = filterEmployeeIds.includes(String(emp.id));
                                            return (
                                                <Box
                                                    key={emp.id}
                                                    px={4}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: "surfaceHover" }}
                                                    onClick={() => {
                                                        setFilterEmployeeIds(prev => {
                                                            const sid = String(emp.id);
                                                            if (prev.includes(sid)) return prev.filter(x => x !== sid);
                                                            return [...prev, sid];
                                                        });
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={3}
                                                >
                                                    <Checkbox
                                                        isChecked={isChecked}
                                                        colorScheme="green"
                                                        borderColor="borderPrimary"
                                                        pointerEvents="none"
                                                    />
                                                    <Avatar size="xs" name={emp.name} src={emp.avatar || emp.url} />
                                                    <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                                                </Box>
                                            );
                                        })}
                                    {(!employees || employees.length === 0) && (
                                        <Box p={4} textAlign="center" color="textMuted" fontSize="sm">
                                            Nenhum funcionário encontrado.
                                        </Box>
                                    )}
                                </Stack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                    <Button
                        leftIcon={<AiOutlinePlus />}
                        size="sm"
                        bg="secondary"
                        color="primary"
                        _hover={{ bg: 'secondaryHover' }}
                        fontWeight="800"
                        onClick={() => handleAbrir()}
                        flexShrink={0}
                    >
                        Novo Contato
                    </Button>
                </Flex>
            </Flex>

            {/* Tabela */}
            <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                <Flex
                    align="center"
                    gap={3}
                    p={4}
                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                    borderBottom="1px solid"
                    borderColor="borderPrimary"
                >
                    <Icon as={AiOutlineContacts} color="secondary" boxSize="20px" />
                    <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1px" fontWeight="800">
                        Base de Contatos
                    </Heading>
                    <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
                        {contatosFiltrados.length}
                    </Badge>
                </Flex>
                <TableContainer>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Contato</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Empresa</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Telefone</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">E-mail</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Canal</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Responsável</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {contatosFiltrados.length === 0 && (
                                <Tr>
                                    <Td colSpan={7} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                                        {(busca || filterEmployeeIds.length > 0) ? 'Nenhum contato encontrado para o filtro atual' : 'Nenhum contato cadastrado'}
                                    </Td>
                                </Tr>
                            )}
                            {paginatedContatos.map(contato => (
                                <Tr
                                    key={contato.id}
                                    _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }}
                                    cursor="pointer"
                                    onClick={() => handleVisualizar(contato)}
                                >
                                    <Td borderColor="borderPrimary">
                                        <HStack spacing={3}>
                                            <Avatar
                                                size="sm"
                                                name={contato.nome}
                                                bg="rgba(2,234,117,0.2)"
                                                color="secondary"
                                                fontWeight="900"
                                                fontSize="xs"
                                            >
                                                {getInitials(contato.nome)}
                                            </Avatar>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="800" fontSize="sm" color="textPrimary">
                                                    {contato.nome}
                                                </Text>
                                                {contato.cargo && (
                                                    <Text fontSize="10px" color="textSecondary">{contato.cargo}</Text>
                                                )}
                                            </VStack>
                                        </HStack>
                                    </Td>
                                    <Td borderColor="borderPrimary">
                                        <Text fontSize="sm" color="textPrimary" fontWeight="600">
                                            {contato.empresa || '—'}
                                        </Text>
                                    </Td>
                                    <Td borderColor="borderPrimary">
                                        {contato.telefone ? (
                                            <HStack spacing={1}>
                                                <Icon as={AiOutlinePhone} color="textSecondary" boxSize="12px" />
                                                <Text fontSize="sm" color="textSecondary">{contato.telefone}</Text>
                                            </HStack>
                                        ) : (
                                            <Text fontSize="sm" color="textSecondary">—</Text>
                                        )}
                                    </Td>
                                    <Td borderColor="borderPrimary">
                                        {contato.email ? (
                                            <HStack spacing={1}>
                                                <Icon as={AiOutlineMail} color="textSecondary" boxSize="12px" />
                                                <Text fontSize="sm" color="textSecondary">{contato.email}</Text>
                                            </HStack>
                                        ) : (
                                            <Text fontSize="sm" color="textSecondary">—</Text>
                                        )}
                                    </Td>
                                    <Td borderColor="borderPrimary">
                                        {contato.canal ? (
                                            <Badge
                                                bg="rgba(66,153,225,0.15)"
                                                color="#4299E1"
                                                fontSize="xs"
                                                fontWeight="700"
                                                borderRadius="md"
                                            >
                                                {contato.canal}
                                            </Badge>
                                        ) : '—'}
                                    </Td>
                                    <Td borderColor="borderPrimary">
                                        <HStack spacing={1.5}>
                                            <Box w="6px" h="6px" borderRadius="full" bg="#9F7AEA" />
                                            <Text fontSize="xs" color="textSecondary" fontWeight="600">
                                                {contato.responsavel || 'Sistema'}
                                            </Text>
                                        </HStack>
                                    </Td>
                                    <Td borderColor="borderPrimary" isNumeric>
                                        <HStack spacing={1} justify="flex-end" onClick={e => e.stopPropagation()}>
                                            <Tooltip label="Visualizar" fontSize="xs">
                                                <IconButton
                                                    aria-label="Visualizar"
                                                    icon={<AiOutlineEye />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textSecondary"
                                                    _hover={{ color: 'secondary', bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }}
                                                    onClick={() => handleVisualizar(contato)}
                                                />
                                            </Tooltip>
                                            <Tooltip label="Editar" fontSize="xs">
                                                <IconButton
                                                    aria-label="Editar"
                                                    icon={<AiOutlineEdit />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textSecondary"
                                                    _hover={{ color: 'secondary', bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }}
                                                    onClick={() => handleAbrir(contato)}
                                                />
                                            </Tooltip>
                                            <Tooltip label="Excluir" fontSize="xs">
                                                <IconButton
                                                    aria-label="Excluir"
                                                    icon={<AiOutlineDelete />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textSecondary"
                                                    _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                    onClick={() => handleAbrirExcluir(contato)}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>

                <TablePagination
                    page={currentPage}
                    pageSize={itemsPerPage}
                    totalItems={contatosFiltrados.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={size => {
                        setItemsPerPage(size);
                        setCurrentPage(1);
                    }}
                />            </Box>

            {/* ── Modal Visualizar Contato ── */}
            <Modal isOpen={isViewOpen} onClose={handleFecharVisualizar} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody pt={8} pb={2} px={6}>
                        {contatoVisualizando && (
                            <VStack spacing={5} align="stretch">
                                {/* Profile header */}
                                <Flex direction="column" align="center" gap={3}>
                                    <Avatar
                                        size="xl"
                                        name={contatoVisualizando.nome}
                                        bg="rgba(2,234,117,0.15)"
                                        color="secondary"
                                        fontWeight="900"
                                        fontSize="2xl"
                                        border="3px solid"
                                        borderColor="rgba(2,234,117,0.3)"
                                    >
                                        {getInitials(contatoVisualizando.nome)}
                                    </Avatar>
                                    <VStack spacing={0}>
                                        <Text fontSize="xl" fontWeight="900" color="textPrimary">
                                            {contatoVisualizando.nome}
                                        </Text>
                                        {contatoVisualizando.cargo && (
                                            <Text fontSize="sm" color="textSecondary" fontWeight="600">
                                                {contatoVisualizando.cargo}
                                            </Text>
                                        )}
                                        {contatoVisualizando.empresa && (
                                            <Badge mt={1} bg={colorMode === 'dark' ? "whiteAlpha.100" : "blackAlpha.100"} color="textSecondary" fontSize="xs" fontWeight="700" borderRadius="md" px={2}>
                                                {contatoVisualizando.empresa}
                                            </Badge>
                                        )}
                                    </VStack>
                                </Flex>

                                <Divider borderColor="borderPrimary" />

                                {/* Details grid */}
                                <SimpleGrid columns={2} spacing={3}>
                                    <DetailItem icon={AiOutlinePhone} label="Telefone" value={contatoVisualizando.telefone} />
                                    <DetailItem icon={AiOutlineMail} label="E-mail" value={contatoVisualizando.email} />
                                    <DetailItem icon={AiOutlineBank} label="Empresa" value={contatoVisualizando.empresa} />
                                    <DetailItem
                                        icon={AiOutlineIdcard}
                                        label="Canal"
                                        value={contatoVisualizando.canal}
                                        valueColor="#4299E1"
                                    />
                                </SimpleGrid>

                                {/* Observações */}
                                {contatoVisualizando.observacoes && (
                                    <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="lg" px={3} py={2.5}>
                                        <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                            Observações
                                        </Text>
                                        <Text fontSize="sm" color="textPrimary" whiteSpace="pre-wrap">
                                            {contatoVisualizando.observacoes}
                                        </Text>
                                    </Box>
                                )}

                                {/* Criado por + data */}
                                <Flex justify="space-between" align="center" px={1}>
                                    <HStack spacing={2}>
                                        <Icon as={AiOutlineUser} color="textSecondary" boxSize="12px" />
                                        <Text fontSize="xs" color="textSecondary">
                                            Responsável <Text as="span" color="textPrimary" fontWeight="700">{contatoVisualizando.responsavel || 'Sistema'}</Text>
                                        </Text>
                                    </HStack>
                                    {contatoVisualizando.criadoEm && (
                                        <HStack spacing={1}>
                                            <Icon as={AiOutlineCalendar} color="textSecondary" boxSize="12px" />
                                            <Text fontSize="xs" color="textSecondary">
                                                {new Date(contatoVisualizando.criadoEm).toLocaleDateString('pt-BR')}
                                            </Text>
                                        </HStack>
                                    )}
                                </Flex>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3} mt={2}>
                        <Button
                            leftIcon={<AiOutlineDelete />}
                            variant="ghost"
                            color="red.400"
                            _hover={{ bg: 'rgba(229,62,62,0.1)' }}
                            onClick={handleExcluirDoView}
                            size="sm"
                        >
                            Excluir
                        </Button>
                        <Box flex={1} />
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFecharVisualizar} size="sm">
                            Fechar
                        </Button>
                        <Button
                            leftIcon={<AiOutlineEdit />}
                            bg="secondary"
                            color="primary"
                            _hover={{ bg: 'secondaryHover' }}
                            fontWeight="800"
                            onClick={handleEditarDoView}
                            size="sm"
                        >
                            Editar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Modal Criar/Editar ── */}
            <Modal isOpen={isOpen} onClose={handleMinimizar} isCentered size="lg" closeOnOverlayClick={true}>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pr="90px">
                        <HStack spacing={3}>
                            <Icon as={AiOutlineContacts} color="secondary" boxSize="20px" />
                            <Text fontWeight="800">{contatoEditando ? 'Editar Contato' : 'Novo Contato'}</Text>
                        </HStack>
                    </ModalHeader>
                    <HStack position="absolute" top={4} right={4} spacing={1} zIndex={10}>
                        <IconButton
                            aria-label="Minimizar"
                            title="Minimizar"
                            icon={<Icon as={AiOutlineMinus} boxSize="16px" />}
                            size="sm"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: "whiteAlpha.200", color: "secondary" }}
                            borderRadius="md"
                            onClick={handleMinimizar}
                        />
                        <ModalCloseButton position="relative" top={0} right={0} color="textSecondary" onClick={handleFechar} />
                    </HStack>
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <HStack spacing={3}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Nome
                                    </FormLabel>
                                    <Input
                                        value={form.nome}
                                        onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                                        placeholder="Nome completo"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Cargo
                                    </FormLabel>
                                    <SearchableSelect
                                        placeholder="Selecione o cargo"
                                        value={form.cargo_id || ''}
                                        onChange={(v, opt) => {
                                            const id = v == null ? null : Number(v);
                                            setForm(p => ({ ...p, cargo_id: id, cargo: opt ? opt.label : '' }));
                                        }}
                                        options={cargos.map(c => ({ value: c.id, label: c.nome }))}
                                        isClearable
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                            </HStack>

                            {/* Autocomplete Empresa */}
                            <FormControl position="relative">
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Empresa (Marcas ERP)
                                </FormLabel>
                                <Input
                                    value={form.empresa}
                                    onChange={e => setForm(p => ({ ...p, empresa: e.target.value, company_id: null }))}
                                    onFocus={() => setEmpresaFocada(true)}
                                    onBlur={() => setTimeout(() => setEmpresaFocada(false), 250)}
                                    placeholder="Nome da empresa (sugere marcas ERP)"
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                                {empresaFocada && marcasSugeridas.length > 0 && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg="surface"
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        borderRadius="md"
                                        zIndex={2000}
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                    >
                                        {marcasSugeridas.map(m => (
                                            <Box
                                                key={m.id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'secondary' }}
                                                onMouseDown={() => {
                                                    setForm(p => ({ ...p, empresa: m.nome, company_id: m.id }));
                                                    setEmpresaFocada(false);
                                                }}
                                                fontSize="sm"
                                                color="textPrimary"
                                            >
                                                {m.nome}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </FormControl>

                            <HStack spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Telefone
                                    </FormLabel>
                                    <Input
                                        value={form.telefone}
                                        onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
                                        placeholder="(00) 00000-0000"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>

                                {/* Autocomplete E-mail do Colaborador (Google/AG_EMPLOYEES) */}
                                <FormControl position="relative">
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        E-mail
                                    </FormLabel>
                                    <Input
                                        value={form.email}
                                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                        onFocus={() => setEmailFocado(true)}
                                        onBlur={() => setTimeout(() => setEmailFocado(false), 250)}
                                        placeholder="email@conteltelecom.com.br"
                                        type="email"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                    {emailFocado && employeesSugeridos.length > 0 && (
                                        <Box
                                            position="absolute"
                                            top="100%"
                                            left={0}
                                            right={0}
                                            bg="surface"
                                            border="1px solid"
                                            borderColor="borderPrimary"
                                            borderRadius="md"
                                            zIndex={2000}
                                            maxH="200px"
                                            overflowY="auto"
                                            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                        >
                                            {employeesSugeridos.map(emp => (
                                                <Box
                                                    key={emp.id}
                                                    px={3}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', color: 'secondary' }}
                                                    onMouseDown={() => {
                                                        setForm(p => ({
                                                            ...p,
                                                            email: emp.email,
                                                            nome: emp.name,
                                                            assignee_id: Number(emp.id)
                                                        }));
                                                        setEmailFocado(false);
                                                    }}
                                                    fontSize="sm"
                                                    color="textPrimary"
                                                >
                                                    <Text fontWeight="800" fontSize="xs">{emp.name}</Text>
                                                    <Text fontSize="10px" color="textSecondary">{emp.email}</Text>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </FormControl>
                            </HStack>

                            <HStack spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Responsável
                                    </FormLabel>
                                    <SearchableSelect
                                        placeholder="Selecione o responsável"
                                        value={form.assignee_id || ''}
                                        onChange={v => setForm(p => ({ ...p, assignee_id: v == null ? null : Number(v) }))}
                                        options={employees.map(e => ({ value: e.id, label: e.name }))}
                                        isClearable
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Canal de Origem
                                    </FormLabel>
                                    <SearchableSelect
                                        placeholder="Selecione o canal"
                                        value={form.source_channel_id || ''}
                                        onChange={(v, opt) => {
                                            const id = v == null ? null : Number(v);
                                            setForm(p => ({ ...p, source_channel_id: id, canal: opt ? opt.label : '' }));
                                        }}
                                        options={canais.map(c => ({ value: c.id, label: c.nome }))}
                                        isClearable
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                            </HStack>

                            <FormControl>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Observações
                                </FormLabel>
                                <Textarea
                                    value={form.observacoes}
                                    onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))}
                                    placeholder="Notas sobre este contato..."
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    rows={3}
                                    resize="vertical"
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFechar} size="sm">
                            Cancelar
                        </Button>
                        <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvar} size="sm">
                            {contatoEditando ? 'Salvar Alterações' : 'Adicionar Contato'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Confirmação padrão de exclusão ── */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={onDelClose}
                onConfirm={handleConfirmarExcluir}
                title="Excluir Contato"
                itemName={contatoExcluindo?.nome}
            />
        </VStack>
    );
};

export default Contatos;
